/* Client-side dismissal of notifications */
import { useCallback, useMemo, useState } from 'react';
import { eLocalStorageKeys } from 'src/constants/generic-constants';
import { load, save } from 'src/utils/local-storage';
import { isDismissible, XptNotification } from './notificationModels';

const loadDismissedIds = (): string[] => {
  const stored = load(eLocalStorageKeys.DismissedNotifications);
  return Array.isArray(stored) ? stored.filter((id): id is string => typeof id === 'string') : [];
};

/**
 * Tracks dismissed notifications.
 *
 * There is no backend delete — notificationService exposes only a read (getMyNotifications)
 * and a read-watermark advance (markMyNotificationsRead). So dismissal is stored locally
 * and is per-browser rather than per-account. A persistent notification represents an
 * action still pending, so it is never dismissible; hiding it would lose the task.
 */
export const useDismissedNotifications = (items: XptNotification[]) => {
  const [dismissedIds, setDismissedIds] = useState<string[]>(loadDismissedIds);

  const dismiss = useCallback((notificationId: string) => {
    setDismissedIds((previous) => {
      if (previous.includes(notificationId)) {
        return previous;
      }
      const next = [...previous, notificationId];
      save(eLocalStorageKeys.DismissedNotifications, next);
      return next;
    });
  }, []);

  const visibleItems = useMemo(() => {
    if (dismissedIds.length === 0) {
      return items;
    }
    const dismissed = new Set(dismissedIds);
    // A persistent item is never hidden, even if its id somehow ended up in the stored list.
    return items.filter((item) => !dismissed.has(item.notification_id) || !isDismissible(item));
  }, [items, dismissedIds]);

  return { visibleItems, dismiss };
};
